/**
 * 브라우저 쪽 구독 절차.
 *
 * 순서가 곧 성패다 — 권한 요청을 네트워크 await 뒤로 미루면 사용자 제스처가 소진되어
 * iOS 에서 권한 요청 자체가 거부된다. 그래서 `requestPermission()` 을 <b>가장 먼저</b> 부른다.
 */

import { api } from '../api';
import { registerPushServiceWorker } from './registerServiceWorker';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/** base64url VAPID 공개키 → subscribe() 가 요구하는 Uint8Array. */
function urlBase64ToUint8Array(base64) {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(normalized);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/**
 * 권한 요청 — 반드시 클릭 핸들러 최상단에서 호출한다.
 * @returns {Promise<'granted'|'denied'|'default'>}
 */
export function requestNotificationPermission() {
    if (!('Notification' in window)) return Promise.resolve('denied');
    // 이미 결정된 권한은 다시 묻지 않는다. denied 는 브라우저 설정에서만 되돌릴 수 있다.
    if (Notification.permission !== 'default') return Promise.resolve(Notification.permission);
    return Notification.requestPermission();
}

/**
 * 이 브라우저의 구독을 만든다(있으면 재사용).
 *
 * `getSubscription()` 을 먼저 보는 이유 — 이미 구독이 있는데 다시 subscribe 하면 브라우저가
 * 같은 endpoint 를 돌려주기도 하고 새로 만들기도 한다. 기존 것을 재사용하면 결과가 일정하다.
 *
 * @param {string} publicKey VAPID 공개키
 * @returns {Promise<PushSubscription>}
 */
export async function createSubscription(publicKey) {
    const registration = await registerPushServiceWorker();
    if (!registration) throw new Error('서비스워커를 등록할 수 없습니다.');
    await navigator.serviceWorker.ready;

    const existing = await registration.pushManager.getSubscription();
    if (existing) return existing;

    return registration.pushManager.subscribe({
        // 규격 요구사항 — 푸시를 받고 알림을 표시하지 않으면 브라우저가 구독을 회수한다.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
}

/**
 * 서버의 VAPID 공개키. 하드코딩하지 않는 이유는 키 교체 시 프론트 재배포를 피하기 위해서다.
 *
 * ★ 경로가 `/public/` 아래인 이유 — QR 로 들어온 폰은 로그인 상태가 아니고, 백엔드는
 *   `/api/**` 에 인증을 걸고 `/api/public/**` 만 제외한다. PC·폰이 같은 엔드포인트를 쓴다.
 *   (공개키는 공개키라 노출돼도 서명을 만들 수 없어 무해하다)
 */
export async function fetchPublicKey() {
    const res = await api.get(`${API_BASE}/public/push/public-key`);
    const data = res?.data ?? res ?? {};
    if (!data.publicKey) throw new Error('휴대폰 알림이 아직 준비되지 않았습니다.');
    return data.publicKey;
}

/**
 * 로그인된 상태에서 이 기기를 등록한다 (PC 용 경로).
 * 폰은 로그인 없이 페어링 코드로 등록한다 — {@link registerWithPairingCode}.
 */
export async function subscribeThisDevice() {
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
        throw new Error(permission === 'denied'
            ? '알림이 차단되어 있습니다. 브라우저 주소창 왼쪽 자물쇠 → 알림 → 허용으로 바꿔주세요.'
            : '알림 권한이 필요합니다.');
    }
    const publicKey = await fetchPublicKey();
    const subscription = await createSubscription(publicKey);
    await api.post(`${API_BASE}/push/subscriptions`, subscription.toJSON());
    return subscription;
}

/**
 * 이 기기의 구독을 해지한다.
 *
 * 브라우저 쪽 `unsubscribe()` 와 서버 행 삭제를 <b>둘 다</b> 해야 한다. 서버만 지우면
 * 브라우저는 endpoint 를 계속 들고 있어, 다시 켤 때 "이미 구독됨" 상태로 꼬인다.
 */
export async function unsubscribeThisDevice() {
    const registration = await navigator.serviceWorker.getRegistration('/');
    const subscription = registration && await registration.pushManager.getSubscription();
    if (!subscription) return false;

    const endpoint = subscription.endpoint;
    // 서버를 먼저 지운다 — 로컬만 지우고 서버 호출이 실패하면 "해지한 줄 알았는데 계속 오는"
    // 상태가 되고, 그때는 endpoint 를 잃어버려 서버 행을 지울 방법이 없다.
    try {
        await api.delete(`${API_BASE}/push/subscriptions`, { data: { endpoint } });
    } finally {
        await subscription.unsubscribe();
    }
    return true;
}

/** 이 브라우저에 살아 있는 구독이 있나 — 토글의 현재 상태 표시용. */
export async function getLocalSubscription() {
    if (!('serviceWorker' in navigator)) return null;
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) return null;
    return registration.pushManager.getSubscription();
}

/**
 * 로컬 구독과 서버 목록을 대조해 어긋난 상태를 바로잡는다.
 *
 * 왜 필요한가 — PC 에서 "이 기기 해지" 를 하면 서버 행만 지워진다(그 폰에 접근할 수 없으므로).
 * 그 폰은 여전히 구독을 들고 있어 토글이 켜진 것처럼 보이는데 알림은 오지 않는다.
 * 앱을 열 때 한 번 대조해 로컬도 정리하면 그 상태가 사라진다.
 *
 * @param {string[]} serverEndpoints 서버에 등록된 endpoint 목록
 */
export async function reconcileWithServer(serverEndpoints) {
    const local = await getLocalSubscription();
    if (!local) return;
    if (serverEndpoints.includes(local.endpoint)) return;
    await local.unsubscribe();
}

/**
 * 폰에서 페어링 코드로 등록한다 — <b>로그인하지 않는다.</b>
 *
 * 코드가 "누구의 구독인지" 만 서버에 알려 준다. 세션이 발급되지 않으므로 이 경로로 할 수 있는
 * 일은 구독 등록 하나뿐이다.
 */
export async function registerWithPairingCode(code) {
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
        throw new Error(permission === 'denied'
            ? '알림이 차단되어 있습니다. 브라우저 설정에서 이 사이트의 알림을 허용해 주세요.'
            : '알림 권한이 필요합니다.');
    }

    // 인증이 없는 엔드포인트라 api 인스턴스(토큰 인터셉터)를 쓰지 않고 직접 호출한다.
    const keyRes = await fetch(`${API_BASE}/public/push/public-key`);
    if (!keyRes.ok) throw new Error('휴대폰 알림이 아직 준비되지 않았습니다.');
    const { publicKey } = await keyRes.json();

    const subscription = await createSubscription(publicKey);

    const res = await fetch(`${API_BASE}/public/push/subscriptions/by-pairing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, subscription: subscription.toJSON() }),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'QR 코드가 만료되었습니다. PC 에서 다시 만들어 주세요.');
    }
    return subscription;
}
